// Minimal PNG decoder for data images (masks, height and distance fields).
//
// A browser only hands image pixels back through a canvas, and a canvas
// stores them premultiplied by alpha: any channel under a low alpha comes
// back quantised or zeroed. The coastline masks keep a second distance field
// in their alpha channel, so they are decoded here instead, byte-exact.
// Handles 8-bit, non-interlaced greyscale, grey + alpha, RGB and RGBA, which
// is everything scripts/fetch-ksc-imagery.mjs writes. Works in the browser
// and in Node 18+ (both have DecompressionStream).

const CHANNELS = { 0: 1, 4: 2, 2: 3, 6: 4 };

async function inflate(bytes) {
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

// bytes: Uint8Array of the whole file. Resolves { width, height, channels, data }.
export async function decodePng(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint32(0) !== 0x89504e47) throw new Error('not a PNG');
  let p = 8, width = 0, height = 0, channels = 0;
  const idat = [];
  let idatLen = 0;
  while (p < bytes.length) {
    const len = view.getUint32(p);
    const type = String.fromCharCode(bytes[p + 4], bytes[p + 5], bytes[p + 6], bytes[p + 7]);
    const body = bytes.subarray(p + 8, p + 8 + len);
    if (type === 'IHDR') {
      width = view.getUint32(p + 8);
      height = view.getUint32(p + 12);
      const depth = body[8], colour = body[9], interlace = body[12];
      channels = CHANNELS[colour];
      if (depth !== 8 || !channels || interlace !== 0) throw new Error(`unsupported PNG (depth ${depth}, colour ${colour}, interlace ${interlace})`);
    } else if (type === 'IDAT') {
      idat.push(body);
      idatLen += len;
    } else if (type === 'IEND') break;
    p += 12 + len;
  }
  const packed = new Uint8Array(idatLen);
  let o = 0;
  for (const c of idat) { packed.set(c, o); o += c.length; }
  const raw = await inflate(packed);
  const stride = width * channels;
  const out = new Uint8Array(stride * height);
  const bpp = channels;
  for (let y = 0; y < height; y++) {
    const filter = raw[y * (stride + 1)];
    const src = y * (stride + 1) + 1;
    const row = y * stride, prev = row - stride;
    for (let i = 0; i < stride; i++) {
      const x = raw[src + i];
      const a = i >= bpp ? out[row + i - bpp] : 0;
      const b = y > 0 ? out[prev + i] : 0;
      let v;
      if (filter === 0) v = x;
      else if (filter === 1) v = x + a;
      else if (filter === 2) v = x + b;
      else if (filter === 3) v = x + ((a + b) >> 1);
      else {
        const c = i >= bpp && y > 0 ? out[prev + i - bpp] : 0;
        const pp = a + b - c, pa = Math.abs(pp - a), pb = Math.abs(pp - b), pc = Math.abs(pp - c);
        v = x + (pa <= pb && pa <= pc ? a : pb <= pc ? b : c);
      }
      out[row + i] = v & 0xff;
    }
  }
  return { width, height, channels, data: out };
}

export async function fetchPng(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
  return decodePng(new Uint8Array(await res.arrayBuffer()));
}
